import { useState, useEffect } from 'react';
import { motion } from 'motion/react';
import { ArrowLeft, Megaphone, Lock, Mic, Image as ImageIcon } from 'lucide-react';
import { Avatar, AvatarImage, AvatarFallback } from './ui/avatar';
import { ScrollArea } from './ui/scroll-area';
import { FlagImage } from './utils/countryFlags';
import { FormattedText } from './FormattedText';
import { ParallaxBackground } from './utils/parallax';
import { useUserData } from './UserDataContext';
import { useContentFilter } from '../hooks/useContentFilter';
import { formatTimeAgo } from './utils/timeFormat';
import { EVENTS } from '../constants/navigation';
import { YapService } from '../services/api';
import { ShoutedYap } from '../types';
import { useScreenReady } from '../contexts/ScreenTransitionContext';

const BLUE = '#4478ea';

interface YapShoutsScreenProps {
  /** The ORIGINAL yap whose shouts are being listed. */
  yapId: string;
  onBack: () => void;
}

/**
 * Every captioned shout of one yap, newest first. Simple reposts are excluded:
 * the screen exists to read what people wrote, and a repost writes nothing.
 *
 * One query, no per-row user lookups: a shout doc denormalizes the shouter's
 * name, username, avatar and flag at creation time. (The likers list does pay an
 * N+1 for exactly this data, because a like record carries only a uid.)
 *
 * Header sizing and safe-area padding are copied from ShoutDetailsScreen so the
 * two read as the same screen family on a notched device.
 *
 * Note on styling: src/index.css is a COMPILED Tailwind snapshot, so a utility
 * that wasn't already in use somewhere at compile time has no rule and renders
 * nothing. Anything load-bearing here (weights, sizes, the blue, the bottom
 * safe-area inset) is therefore inline, and every class kept below was checked
 * against that stylesheet.
 */
export function YapShoutsScreen({ yapId, onBack }: YapShoutsScreenProps) {
  const [shouts, setShouts] = useState<ShoutedYap[]>([]);
  const [loading, setLoading] = useState(true);
  useScreenReady(!loading);
  const { userProfile, isFriend } = useUserData();
  const { filterVisible } = useContentFilter();

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    (async () => {
      const fetched = await YapService.fetchShoutsForYap(yapId);
      if (!cancelled) {
        setShouts(fetched);
        setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [yapId]);

  // Blocked shouters and muted captions drop out via the shared filter; the
  // friends-only rule is applied here with the same test the world feed uses —
  // a friends-only shout is addressed to the SHOUTER's friends, so it matches on
  // shoutedBy rather than on the original yap's author.
  const visibleShouts = filterVisible(shouts).filter((s: any) => {
    if (s.visibility !== 'friends' && !s.friendsOnly) return true;
    const shouterId = String(s.shoutedBy || '');
    if (!shouterId) return false;
    return shouterId === String(userProfile?.id) || isFriend(shouterId);
  });

  const openShout = (shout: any) => {
    window.dispatchEvent(new CustomEvent(EVENTS.NAVIGATE_TO_SHOUT_DETAIL, { detail: shout }));
  };

  const openShouter = (e: React.MouseEvent, shout: any) => {
    e.stopPropagation();
    const id = String(shout.shoutedBy || '');
    if (!id) return;
    window.dispatchEvent(new CustomEvent(EVENTS.NAVIGATE_TO_FRIEND_PROFILE, { detail: { id } }));
  };

  return (
    <div className="w-full h-screen relative bg-light-pattern dark:bg-[#0D0D0D]">
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <ParallaxBackground speed={0.05} />
      </div>
      <div className="w-full max-w-2xl mx-auto h-full flex flex-col relative z-10 pb-20">
        <div
          className="pb-4 px-4 border-b border-black/10 dark:border-white/10 bg-[#F5F5DC]/80 dark:bg-[#121212] backdrop-blur-sm"
          style={{ paddingTop: 'max(60px, calc(env(safe-area-inset-top) + 24px))' }}
        >
          <div className="flex items-center gap-4">
            <motion.button
              whileTap={{ scale: 0.95 }}
              onClick={onBack}
              className="text-black dark:text-[#E5E5E5] hover:text-[#F39C12] dark:hover:text-white"
              aria-label="Back"
            >
              <ArrowLeft className="w-6 h-6" />
            </motion.button>
            <div className="min-w-0">
              <h1 className="text-2xl text-black dark:text-white">Shouts</h1>
              {!loading && visibleShouts.length > 0 && (
                <p className="text-[#666666] dark:text-[#a0a0a0]" style={{ fontSize: 12 }}>
                  {visibleShouts.length} {visibleShouts.length === 1 ? 'shout' : 'shouts'}
                </p>
              )}
            </div>
          </div>
        </div>

        <ScrollArea className="flex-1" style={{ minHeight: 0 }}>
          <div
            className="p-4 space-y-3"
            style={{ paddingBottom: 'calc(env(safe-area-inset-bottom) + 80px)' }}
          >
            {loading ? (
              <div
                className="flex flex-col items-center justify-center text-[#666666] dark:text-[#a0a0a0]"
                style={{ paddingTop: 64, paddingBottom: 64 }}
              >
                <motion.div
                  animate={{ rotate: 360 }}
                  transition={{ duration: 2, repeat: Infinity, ease: 'linear' }}
                >
                  <Megaphone className="w-8 h-8" />
                </motion.div>
                <p className="text-xs mt-2" style={{ fontWeight: 700 }}>Loading shouts...</p>
              </div>
            ) : visibleShouts.length === 0 ? (
              <div
                className="text-center text-[#666666] dark:text-[#a0a0a0]"
                style={{ paddingTop: 64, paddingBottom: 64 }}
              >
                <Megaphone className="w-12 h-12 mx-auto mb-3" style={{ opacity: 0.2 }} />
                <p className="text-sm" style={{ fontWeight: 700 }}>Nothing to read yet</p>
                <p className="text-xs mt-1" style={{ opacity: 0.7 }}>Shouts with a caption will show up here.</p>
              </div>
            ) : (
              visibleShouts.map((shout: any) => {
                const name = shout.shoutedByName || 'User';
                const username = String(shout.shoutedByUsername || '').replace(/^@/, '');
                const flag = shout.shoutedByCountryFlag || shout.shoutedByCountry || '';
                const caption = shout.shoutCaption || '';
                const imageCount = Array.isArray(shout.shoutImages) ? shout.shoutImages.filter(Boolean).length : 0;
                const isFriendsOnly = shout.visibility === 'friends' || shout.friendsOnly === true;

                return (
                  <div
                    key={String(shout.id)}
                    onClick={() => openShout(shout)}
                    className="p-4 rounded-2xl bg-[#F5F5F5] dark:bg-[#1a1a1a] border border-black/10 dark:border-white/10 cursor-pointer"
                  >
                    <div className="flex items-start gap-3">
                      <Avatar
                        className="shrink-0 cursor-pointer"
                        style={{ width: 44, height: 44, border: `2px solid ${BLUE}` }}
                        onClick={(e: React.MouseEvent) => openShouter(e, shout)}
                      >
                        <AvatarImage src={shout.shoutedByAvatar || ''} />
                        <AvatarFallback>{name[0] || '?'}</AvatarFallback>
                      </Avatar>

                      <div className="min-w-0 flex-1">
                        <div
                          className="flex items-center gap-1.5 w-fit cursor-pointer"
                          onClick={(e: React.MouseEvent) => openShouter(e, shout)}
                        >
                          <h4
                            className="text-black dark:text-white truncate"
                            style={{ fontWeight: 700, lineHeight: 1.25 }}
                          >
                            {name}
                          </h4>
                          {flag && (
                            <FlagImage
                              countryName={flag}
                              size="w80"
                              className="rounded shrink-0"
                              style={{ width: 16, height: 'auto', objectFit: 'contain' }}
                            />
                          )}
                        </div>

                        <div className="flex items-center gap-1.5 text-xs text-[#666666] dark:text-[#a0a0a0] mt-0.5">
                          {username && <span className="truncate">@{username}</span>}
                          {username && <span className="shrink-0" style={{ fontSize: 10, opacity: 0.4 }}>•</span>}
                          <span className="whitespace-nowrap shrink-0">
                            {shout.time || formatTimeAgo(shout.timestamp)}
                          </span>
                        </div>

                        <FormattedText
                          text={caption}
                          className="text-[#333333] dark:text-[#E5E5E5] mt-2 whitespace-pre-wrap block"
                        />

                        {(imageCount > 0 || shout.audioUrl || isFriendsOnly) && (
                          <div className="flex items-center gap-2 flex-wrap mt-2">
                            {imageCount > 0 && (
                              <span
                                className="flex items-center gap-1 text-xs px-2 py-1 rounded-full"
                                style={{ background: 'rgba(68,120,234,0.12)', color: BLUE }}
                              >
                                <ImageIcon className="w-3 h-3" />
                                {imageCount} {imageCount === 1 ? 'photo' : 'photos'}
                              </span>
                            )}
                            {shout.audioUrl && (
                              <span
                                className="flex items-center gap-1 text-xs px-2 py-1 rounded-full"
                                style={{ background: 'rgba(68,120,234,0.12)', color: BLUE }}
                              >
                                <Mic className="w-3 h-3" />
                                Voice
                              </span>
                            )}
                            {isFriendsOnly && (
                              <span className="flex items-center gap-1 text-xs px-2 py-1 rounded-full bg-[#E74C3C]/20 dark:bg-[#DC2626]/30 text-[#E74C3C] dark:text-[#DC2626]">
                                <Lock className="w-3 h-3" />
                                Friends Only
                              </span>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </ScrollArea>
      </div>
    </div>
  );
}

export default YapShoutsScreen;
